/**
 * CARDS HOOK - API Methods & React Query Hooks Summary
 *
 * ===========================================
 * API METHODS (Internal Functions):
 * ===========================================
 * • getAllCards()          - GET /billing/cards - Fetch all user cards
 * • createCard()           - POST /billing/cards - Create new card with Stripe SetupIntent
 * • getCardById()          - GET /billing/cards/:id - Fetch specific card by ID
 * • updateCard()           - PATCH /billing/cards/:id - Update card details (DB + Stripe)
 * • deleteCard()           - DELETE /billing/cards/:id - Delete card (DB + Stripe)
 * • createCardRequest()    - POST /cards/:service/requests - Create card request + todo
 * • getCardRequests()      - GET /cards/:service/requests - Fetch card requests by service
 * • getCardBalance()       - GET /cards/:service/:cardId/balance - Get card balance by card ID
 * • resetCardPin()         - POST /cards/:service/:cardId/reset-pin - Reset card PIN code
 * • resetCardEPin()        - POST /cards/:service/:cardId/reset-e-pin - Reset card e-PIN via SMS
 * • activateCard()         - POST /cards/:service/:cardId/activate - Activate card by ID
 * • deactivateCard()       - POST /cards/:service/:cardId/deactivate - Deactivate card by ID
 * • freezeCard()           - POST /cards/:service/:cardId/freeze - Freeze card (block temporarily)
 * • unfreezeCard()         - POST /cards/:service/:cardId/unfreeze - Unfreeze a frozen card
 * • createCardTopUp()      - POST /cards/:service/:cardId/top-up - Create card top-up request
 * • updateCardTopUpStatus() - POST /cards/:service/top-up/:cardTopUpId/update-status - Update top-up status
 * • getCardTopUpRequests() - GET /cards/:service/top-up/requests - Get paginated card top-up requests
 * • getCardTransactions()  - GET /cards/:service/:cardId/transactions - Get paginated card transactions
 * • exportCardTransactions() - GET /cards/:service/:cardId/transactions/export - Export card transactions (CSV/PDF)
 * • getNativeCards()       - GET /cards/:service - Get native cards for authenticated user by service
 *
 * ===========================================
 * REACT QUERY HOOKS (Exported):
 * ===========================================
 * • useBillingCards(enabled?)             - Query: Get all billing cards
 * • useBillingCard(cardId, enabled?)      - Query: Get billing card by ID
 * • useCreateBillingCard()                - Mutation: Create new billing card
 * • useUpdateBillingCard()                - Mutation: Update billing card details
 * • useDeleteBillingCard()                - Mutation: Delete billing card
 * • useCreateCardRequest()         - Mutation: Create card request
 * • useCardRequests(service, enabled?) - Query: Get card requests by service
 * • useUnavailableCardCountries(service, enabled?) - Query: Countries blocked for card delivery
 * • useCardBalance(service, cardId, enabled?) - Query: Get card balance
 * • useResetCardPin()              - Mutation: Reset card PIN
 * • useResetCardEPin()             - Mutation: Reset card e-PIN via SMS
 * • useActivateCard()              - Mutation: Activate card
 * • useDeactivateCard()            - Mutation: Deactivate card
 * • useFreezeCard()                - Mutation: Freeze card (block temporarily)
 * • useUnfreezeCard()              - Mutation: Unfreeze a frozen card
 * • useCreateCardTopUp()           - Mutation: Create card top-up request
 * • useUpdateCardTopUpStatus()     - Mutation: Update card top-up status
 * • useCardTopUpRequests(service, filters?, enabled?) - Query: Get card top-up requests
 * • useCardTransactions(service, cardId, filters?, enabled?) - Query: Get card transactions
 * • useExportCardTransactions()    - Mutation: Export card transactions as CSV or PDF
 * • useNativeCards(service, filters?, enabled?) - Query: Get native cards by service
 *
 * ===========================================
 * KEY FEATURES:
 * ===========================================
 * • Authentication via Bearer token (store + cookie fallback)
 * • Automatic query invalidation on mutations
 * • 5-minute stale time with disabled auto-refetch
 * • Comprehensive error handling with typed responses
 * • Support for multiple card services (e.g., "eps")
 * • Stripe integration for card management
 * ===========================================
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import fetchAPI from '@/lib/fetch-api';
import type { Card } from '@/types/card';

// Export the NativeCard interface for use in other files
export type { NativeCard, NativeCardsFilters };

// ==================== INTERFACES ====================

interface CreateCardRequest {
  intent_id: string;
  card_holder: string;
  address_line1: string;
  address_line2?: string;
  address_city: string;
  address_state: string;
  address_zip: string;
  address_country: string;
}

interface UpdateCardRequest {
  card_holder?: string;
  expiry_month?: number;
  expiry_year?: number;
  is_active?: boolean;
  is_default?: boolean;
  address_line1?: string;
  address_line2?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  address_country?: string;
}

interface CreateCardRequestRequest {
  first_name: string;
  last_name: string;
  gender: string;
  title: string;
  birthday: string; // DD-MM-YYYY format
  phone: string;
  address: string;
  city: string;
  state: string;
  post_code: string;
  country: string; // ISO code like "GR"
  country_of_birth: string; // ISO code like "GR"
  card_type: string;
  currency: string; // "EUR"
  name_on_card?: string;
}

interface CardRequest {
  id: number;
  user_id: number;
  status: string;
  first_name: string;
  last_name: string;
  gender: string;
  title: string;
  birthday: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  post_code: string;
  country_id: string;
  country_of_birth_id: string;
  card_type: string;
  currency: string;
  reference_number: string | null;
  tracking_number: string | null;
  tracking_link: string | null;
  name_on_card: string;
  created_at: string;
  date: string;
  updated_at: string;
  eps_status: string;
  cid_number: string;
}

interface CardBalance {
  available_balance: number;
  reserved_balance: number;
  current_balance: number;
  currency: string;
  last_updated_at: string;
}

interface CreateCardTopUpRequest {
  amount: number;
  wallet_id: number;
}

interface UpdateCardTopUpStatusRequest {
  status: 'paid' | 'pending' | 'declined';
  admin_note?: string;
}

interface CardTopUpRequest {
  id: number;
  service: string;
  user_id: number;
  card_id: number;
  wallet_id: number;
  amount: number;
  currency_id: number;
  status: 'new' | 'pending' | 'paid' | 'declined';
  created_at: string;
  updated_at: string;
}

interface CardTopUpRequestsFilters {
  service?: string;
  user_id?: number;
  per_page?: number;
  page?: number;
  status?: 'new' | 'pending' | 'paid' | 'declined';
  date?: string;
  currency_id?: number;
  sort_by?: 'newest' | 'oldest';
}

export interface CardTransaction {
  id: string;
  referenceId: string | number;
  name: string;
  description: string;
  amount: string;
  amountConfirmed: string;
  currency: string;
  amountInCardCurrency: string | null;
  cardCurrency: string | null;
  createdAt: string;
  status: CardTransactionStatus['status'];
  type: CardTransactionType['type'];
  transaction_type: string;
  expenses?: unknown[];
  bank_meta?: unknown[];
  admin_note: string | null;
  transaction_category?: string;
  expenseType: string;
  transaction_name: string;
  incoming: boolean;
  category?: string;
  client_code?: string | null;
  department?: string | null;
  local_currency?: string | null;
  fee?: number;
  date?: string;
  created_at?: string;
  card_transaction_id?: string;
  declined_at: string | null;
  paid_at: string | null;
  last4?: string;
  card_number?: string;
}

export interface CardTransactionsPagination {
  currentPage: number;
  perPage: number;
  total: number;
  lastPage: number;
  nextPageUrl: string | null;
  prevPageUrl: string | null;
}

export interface CardTransactionType {
  type: 'card-transaction' | 'top-up' | 'withdraw' | 'atm-fee';
}

export interface CardTransactionStatus {
  status: 'pending' | 'declined' | 'completed' | 'reserved';
}

export interface CardTransactionsFilters {
  page?: number;
  size?: number;
  date_from?: string;
  date_to?: string;
  type?: CardTransactionType[];
  status?: CardTransactionStatus[];
  search?: string;
}

export type CardTransactionsExportFormat = 'csv' | 'pdf';

interface ExportCardTransactionsRequest {
  service: string;
  cardId: string;
  format: CardTransactionsExportFormat;
  filters: CardTransactionsFilters;
}

interface NativeCard {
  id: number;
  card_request_id: number;
  user_id: number;
  currency: string;
  expiration: string;
  exp_year: string;
  exp_month: string;
  last4: string;
  holder: string;
  eps_card_id: string;
  eps_account_id: string;
  eps_tenant_id: string;
  eps_status: string;
  eps_status_id: number;
  eps_alias: string;
  is_virtual: boolean;
  activation_request_sent: boolean;
  created_at: string | null;
  updated_at: string | null;
}

interface NativeCardsFilters {
  page?: number;
  size?: number;
}

// ==================== API FUNCTIONS ====================

/**
 * Retrieves all cards for the authenticated user
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<Card[]>} The list of cards
 */
const getAllCards = async (): Promise<Card[]> => {
  const response = await fetchAPI<Card[]>('/billing/cards', {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch cards');
  }

  return response.data!;
};

/**
 * Stores a new card associated with the authenticated user using a valid Stripe SetupIntent
 * @param {CreateCardRequest} cardData - The card data to store
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<Card[]>} The card creation response
 */
const createCard = async (cardData: CreateCardRequest): Promise<Card[]> => {
  const response = await fetchAPI<Card[]>('/billing/cards', {
    method: 'POST',
    body: JSON.stringify(cardData),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to create card');
  }

  return response.data!;
};

/**
 * Retrieves a specific card for the authenticated user
 * @param {string} cardId - The ID of the card to retrieve
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<Card>} The card data
 */
const getCardById = async (cardId: string): Promise<Card> => {
  const response = await fetchAPI<Card>(`/billing/cards/${cardId}`, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch card');
  }

  return response.data!;
};

/**
 * Updates the details of a specific card associated with the authenticated user in both the database and Stripe
 * @param {string} cardId - The ID of the card to update
 * @param {UpdateCardRequest} updateData - The card data to update
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<void>} The card update response
 */
const updateCard = async (cardId: string, updateData: UpdateCardRequest): Promise<void> => {
  const response = await fetchAPI<void>(`/billing/cards/${cardId}`, {
    method: 'PATCH',
    body: JSON.stringify(updateData),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to update card');
  }
};

/**
 * Deletes a specific card associated with the authenticated user from both the database and Stripe
 * @param {string} cardId - The ID of the card to delete
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<void>} The card deletion response
 */
const deleteCard = async (cardId: string): Promise<void> => {
  const response = await fetchAPI<void>(`/billing/cards/${cardId}`, {
    method: 'DELETE',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to delete card');
  }
};

/**
 * Creates a new card request and a corresponding to-do request for the authenticated user
 * @param {string} service - The service type for the card request (e.g., "eps")
 * @param {CreateCardRequestRequest} requestData - The card request data
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<void>} The card request creation response
 */
const createCardRequest = async (
  service: string,
  requestData: CreateCardRequestRequest
): Promise<void> => {
  const response = await fetchAPI<void>(`/cards/${service}/requests`, {
    method: 'POST',
    body: JSON.stringify(requestData),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to create card request');
  }
};

/**
 * Retrieves all card requests for the authenticated user for a specific service
 * @param {string} service - The service type for the card requests (e.g., "eps")
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<CardRequest[]>} The list of card requests
 */
const getCardRequests = async (service: string): Promise<CardRequest[]> => {
  const response = await fetchAPI<CardRequest[]>(`/cards/${service}/requests`, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch card requests');
  }

  return response.data!;
};

/**
 * Retrieves countries where card delivery is unavailable for a specific service
 * @param {string} service - The card service type (e.g., "eps")
 * @throws {Error} If the API request fails or returns an invalid response
 * @returns {Promise<string[]>} ISO country codes blocked for card delivery
 */
const getUnavailableCardCountries = async (service: string): Promise<string[]> => {
  const response = await fetchAPI<string[]>(`/cards/${service}/unavailable-countries`, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(
      response.error || response.message || 'Failed to fetch unavailable card countries'
    );
  }

  if (!response.data || !Array.isArray(response.data)) {
    throw new Error('Invalid unavailable card countries response');
  }

  return response.data.map((countryCode) => countryCode.toLowerCase());
};

/**
 * Retrieves card balance for a specific card
 * @param {string} service - The service type for the card (e.g., "eps")
 * @param {string} cardId - The ID of the card
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<CardBalance>} The card balance data
 */
const getCardBalance = async (service: string, cardId: string): Promise<CardBalance> => {
  const response = await fetchAPI<CardBalance>(`/cards/${service}/${cardId}/balance`, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch card balance');
  }

  return response.data!;
};

/**
 * Resets card PIN code
 * @param {string} service - The service type for the card (e.g., "eps")
 * @param {string} cardId - The ID of the card
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<void>} The reset PIN response
 */
const resetCardPin = async (service: string, cardId: string): Promise<void> => {
  const response = await fetchAPI<void>(`/cards/${service}/${cardId}/reset-pin`, {
    method: 'POST',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to reset card PIN');
  }
};

/**
 * Resets card e-PIN code. A new e-PIN will be sent to the EPS phone number via SMS.
 * @param {string} service - The service type for the card (e.g., "eps")
 * @param {string} cardId - The ID of the card
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<void>} The reset e-PIN response
 */
const resetCardEPin = async (service: string, cardId: string): Promise<void> => {
  const response = await fetchAPI<void>(`/cards/${service}/${cardId}/reset-e-pin`, {
    method: 'POST',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to reset card e-PIN');
  }
};

/**
 * Activates a card by ID
 * @param {string} service - The service type for the card (e.g., "eps")
 * @param {string} cardId - The ID of the card to activate
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<void>} The activation response
 */
const activateCard = async (service: string, cardId: string): Promise<void> => {
  const response = await fetchAPI<void>(`/cards/${service}/${cardId}/activate`, {
    method: 'POST',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to activate card');
  }
};

/**
 * Deactivates a card by ID
 * @param {string} service - The service type for the card (e.g., "eps")
 * @param {string} cardId - The ID of the card to deactivate
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<void>} The deactivation response
 */
const deactivateCard = async (service: string, cardId: string): Promise<void> => {
  const response = await fetchAPI<void>(`/cards/${service}/${cardId}/deactivate`, {
    method: 'POST',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to deactivate card');
  }
};

/**
 * Freezes an active EPS card, immediately blocking all transactions.
 * Card status changes to 'BlockedByCustomer'.
 * Can be reversed using the unfreeze endpoint.
 * @param {string} service - The service type for the card (e.g., "eps")
 * @param {string} cardId - The ID of the card to freeze
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<void>} The freeze response
 */
const freezeCard = async (service: string, cardId: string): Promise<void> => {
  const response = await fetchAPI<void>(`/cards/${service}/${cardId}/freeze`, {
    method: 'POST',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to freeze card');
  }
};

/**
 * Unfreezes a card that was previously frozen by the customer.
 * Card status changes back to 'Active'.
 * Can only unfreeze cards in 'BlockedByCustomer' status.
 * @param {string} service - The service type for the card (e.g., "eps")
 * @param {string} cardId - The ID of the card to unfreeze
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<void>} The unfreeze response
 */
const unfreezeCard = async (service: string, cardId: string): Promise<void> => {
  const response = await fetchAPI<void>(`/cards/${service}/${cardId}/unfreeze`, {
    method: 'POST',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to unfreeze card');
  }
};

/**
 * Creates a top-up request for a specific card
 * @param {string} service - The service type for the card (e.g., "eps")
 * @param {string} cardId - The ID of the card
 * @param {CreateCardTopUpRequest} topUpData - The top-up request data
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<void>} The top-up creation response
 */
const createCardTopUp = async (
  service: string,
  cardId: string,
  topUpData: CreateCardTopUpRequest
): Promise<void> => {
  const response = await fetchAPI<void>(`/cards/${service}/${cardId}/top-up`, {
    method: 'POST',
    body: JSON.stringify(topUpData),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to create card top-up');
  }
};

/**
 * Updates the status of a card top-up request
 * @param {string} service - The service type for the card (e.g., "eps")
 * @param {string} cardTopUpId - The ID of the card top-up request
 * @param {UpdateCardTopUpStatusRequest} statusData - The status update data
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<void>} The status update response
 */
const updateCardTopUpStatus = async (
  service: string,
  cardTopUpId: string,
  statusData: UpdateCardTopUpStatusRequest
): Promise<void> => {
  const response = await fetchAPI<void>(`/cards/${service}/top-up/${cardTopUpId}/update-status`, {
    method: 'POST',
    body: JSON.stringify(statusData),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to update card top-up status');
  }
};

/**
 * Retrieves paginated list of card top-up requests
 * @param {string} service - The service type for the card (e.g., "eps")
 * @param {CardTopUpRequestsFilters} filters - Optional filters for the request
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<CardTopUpRequest[]>} The list of card top-up requests
 */
const getCardTopUpRequests = async (
  service: string,
  filters: CardTopUpRequestsFilters = {}
): Promise<CardTopUpRequest[]> => {
  // Build query parameters
  const queryParams = new URLSearchParams();
  if (filters.user_id) queryParams.append('user_id', filters.user_id.toString());
  if (filters.per_page) queryParams.append('per_page', filters.per_page.toString());
  if (filters.page) queryParams.append('page', filters.page.toString());
  if (filters.status) queryParams.append('status', filters.status);
  if (filters.date) queryParams.append('date', filters.date);
  if (filters.currency_id) queryParams.append('currency_id', filters.currency_id.toString());
  if (filters.sort_by) queryParams.append('sort_by', filters.sort_by);

  const url = `/cards/${service}/top-up/requests${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const response = await fetchAPI<CardTopUpRequest[]>(url, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch card top-up requests');
  }

  return response.data!;
};

/**
 * Retrieves a list of native cards for the authenticated user for a specific service
 * @param {string} service - The service type for the cards (e.g., "eps")
 * @param {NativeCardsFilters} filters - Optional filters for pagination
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<NativeCard[]>} The list of native cards
 */
const getNativeCards = async (
  service: string,
  filters: NativeCardsFilters = {}
): Promise<NativeCard[]> => {
  // Build query parameters
  const queryParams = new URLSearchParams();
  if (filters.page) queryParams.append('page', filters.page.toString());
  if (filters.size) queryParams.append('size', filters.size.toString());

  const url = `/cards/${service}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const response = await fetchAPI<NativeCard[]>(url, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch native cards');
  }

  return response.data!;
};

/**
 * Retrieves paginated list of transactions for a specific card
 * @param {string} service - The service type for the card (e.g., "eps")
 * @param {string} cardId - The ID of the card
 * @param {CardTransactionsFilters} filters - Optional filters for the request
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<{data: CardTransaction[], pagination: CardTransactionsPagination}>} The list of card transactions with pagination
 */
const getCardTransactions = async (
  service: string,
  cardId: string,
  filters: CardTransactionsFilters = {}
): Promise<{ data: CardTransaction[]; pagination: CardTransactionsPagination }> => {
  // Build query parameters
  const queryParams = buildCardTransactionsQueryParams(filters);

  const url = `/cards/${service}/${cardId}/transactions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const response = await fetchAPI<CardTransaction[]>(url, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch card transactions');
  }

  // fetchAPI returns ApiResponse which has data and pagination at the top level
  // Convert pagination from snake_case (API) to camelCase (our interface)
  const apiPagination = response.pagination;
  const pagination: CardTransactionsPagination = apiPagination
    ? {
        currentPage: apiPagination.current_page,
        perPage: apiPagination.per_page,
        total: apiPagination.total,
        lastPage: apiPagination.last_page,
        nextPageUrl: apiPagination.next_page_url || null,
        prevPageUrl: null,
      }
    : {
        currentPage: 1,
        perPage: filters.size || 10,
        total: 0,
        lastPage: 1,
        nextPageUrl: null,
        prevPageUrl: null,
      };

  return {
    data: response.data || [],
    pagination,
  };
};

/**
 * Builds the query parameters for the card transactions list and export
 * @param {CardTransactionsFilters} filters - Filters for the request
 * @returns {URLSearchParams} The query parameters
 */
function buildCardTransactionsQueryParams(filters: CardTransactionsFilters): URLSearchParams {
  const queryParams = new URLSearchParams();
  if (filters.page) queryParams.append('page', filters.page.toString());
  if (filters.size) queryParams.append('size', filters.size.toString());
  if (filters.date_from) queryParams.append('date_from', filters.date_from);
  if (filters.date_to) queryParams.append('date_to', filters.date_to);
  if (filters.search) queryParams.append('search', filters.search);

  // Handle array parameters - use bracket notation so PHP/Laravel receives them as arrays
  if (filters.type && filters.type.length > 0) {
    filters.type.forEach((t) => queryParams.append('type[]', t.type));
  }
  if (filters.status && filters.status.length > 0) {
    filters.status.forEach((s) => queryParams.append('status[]', s.status));
  }

  return queryParams;
}

/**
 * Exports the card transactions matching the given filters as a CSV or PDF file
 * @param {ExportCardTransactionsRequest} request - Card, file format and table filters (without pagination)
 * @throws {Error} If the API request fails
 * @returns {Promise<Blob>} The exported file
 */
const exportCardTransactions = async ({
  service,
  cardId,
  format,
  filters,
}: ExportCardTransactionsRequest): Promise<Blob> => {
  const queryParams = buildCardTransactionsQueryParams(filters);
  queryParams.append('format', format);

  const response = await fetchAPI<Blob>(
    `/cards/${service}/${cardId}/transactions/export?${queryParams.toString()}`,
    {
      method: 'GET',
      responseType: 'blob',
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to export card transactions');
  }

  return response.data!;
};

// ==================== REACT QUERY HOOKS ====================

/**
 * Hook to fetch all billing cards for the authenticated user
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for billing cards list
 */
export function useBillingCards(enabled: boolean = true): UseQueryResult<Card[], Error> {
  return useQuery({
    queryKey: ['cards'],
    queryFn: getAllCards,
    enabled,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * Hook to create a new billing card for the authenticated user
 * @returns {UseMutationResult} Mutation result for creating a billing card
 */
export function useCreateBillingCard(): UseMutationResult<Card[], Error, CreateCardRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCard,
    onSuccess: () => {
      // Invalidate cards-related queries
      queryClient.invalidateQueries({ queryKey: ['cards'] });
    },
  });
}

/**
 * Hook to fetch a specific billing card by ID for the authenticated user
 * @param {string} cardId - The ID of the card to fetch
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for the specific billing card
 */
export function useBillingCard(
  cardId: string,
  enabled: boolean = true
): UseQueryResult<Card, Error> {
  const query = useQuery({
    queryKey: ['cards', cardId],
    queryFn: () => getCardById(cardId),
    enabled: enabled && !!cardId,
    staleTime: 300000, // 5 minutes
    gcTime: 300000, // 5 minutes garbage collection time
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });

  // Debug logging
  console.log(`[useCard] Query for cardId: ${cardId}`, {
    enabled: enabled && !!cardId,
    data: query.data ? 'loaded' : 'not loaded',
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error?.message,
    queryKey: ['cards', cardId],
  });

  return query;
}

/**
 * Hook to update a specific billing card for the authenticated user
 * @returns {UseMutationResult} Mutation result for updating a billing card
 */
export function useUpdateBillingCard(): UseMutationResult<
  void,
  Error,
  { cardId: string; updateData: UpdateCardRequest }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, updateData }) => updateCard(cardId, updateData),
    onSuccess: (_, { cardId }) => {
      // Invalidate cards-related queries
      queryClient.invalidateQueries({ queryKey: ['cards', cardId] });
    },
  });
}

/**
 * Hook to delete a specific billing card for the authenticated user
 * @returns {UseMutationResult} Mutation result for deleting a billing card
 */
export function useDeleteBillingCard(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCard,
    onSuccess: (_, cardId) => {
      // Invalidate cards-related queries and remove the deleted card from cache
      queryClient.invalidateQueries({ queryKey: ['cards', cardId] });
      queryClient.removeQueries({ queryKey: ['cards', cardId] });
    },
  });
}

/**
 * Hook to create a card request for a specific service
 * @returns {UseMutationResult} Mutation result for creating a card request
 */
export function useCreateCardRequest(): UseMutationResult<
  void,
  Error,
  { service: string; requestData: CreateCardRequestRequest }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ service, requestData }) => createCardRequest(service, requestData),
    onSuccess: (_, { service }) => {
      // Invalidate card requests queries
      queryClient.invalidateQueries({ queryKey: ['card-requests', service] });
      // Invalidate native cards to update card status components
      queryClient.invalidateQueries({ queryKey: ['native-cards', service] });
    },
  });
}

/**
 * Hook to fetch card requests for a specific service
 * @param {string} service - The service type for the card requests (e.g., "eps")
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for card requests list
 */
export function useCardRequests(
  service: string,
  enabled: boolean = true
): UseQueryResult<CardRequest[], Error> {
  return useQuery({
    queryKey: ['card-requests', service],
    queryFn: () => getCardRequests(service),
    enabled: enabled && !!service,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * Hook to fetch countries where card delivery is unavailable for a specific service
 * @param {string} service - The card service type (e.g., "eps")
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for unavailable card countries
 */
export function useUnavailableCardCountries(
  service: string,
  enabled: boolean = true
): UseQueryResult<string[], Error> {
  return useQuery({
    queryKey: ['unavailable-card-countries', service],
    queryFn: () => getUnavailableCardCountries(service),
    enabled: enabled && !!service,
    staleTime: 300000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to fetch card balance for a specific card
 * @param {string} service - The service type for the card (e.g., "eps")
 * @param {string} cardId - The ID of the card
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for card balance
 */
export function useCardBalance(
  service: string,
  cardId: string,
  enabled: boolean = true
): UseQueryResult<CardBalance, Error> {
  return useQuery({
    queryKey: ['card-balance', service, cardId],
    queryFn: () => getCardBalance(service, cardId),
    enabled: enabled && !!service && !!cardId,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * Hook to reset card PIN
 * @returns {UseMutationResult} Mutation result for resetting card PIN
 */
export function useResetCardPin(): UseMutationResult<
  void,
  Error,
  { service: string; cardId: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ service, cardId }) => resetCardPin(service, cardId),
    onSuccess: (_, { service, cardId }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['card-balance', service, cardId] });
      queryClient.invalidateQueries({ queryKey: ['native-cards', service] });
    },
  });
}

/**
 * Hook to reset card e-PIN. A new e-PIN will be sent to the EPS phone number via SMS.
 * @returns {UseMutationResult} Mutation result for resetting card e-PIN
 */
export function useResetCardEPin(): UseMutationResult<
  void,
  Error,
  { service: string; cardId: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ service, cardId }) => resetCardEPin(service, cardId),
    onSuccess: (_, { service, cardId }) => {
      queryClient.invalidateQueries({ queryKey: ['card-balance', service, cardId] });
      queryClient.invalidateQueries({ queryKey: ['native-cards', service] });
    },
  });
}

/**
 * Hook to activate a card
 * @returns {UseMutationResult} Mutation result for activating a card
 */
export function useActivateCard(): UseMutationResult<
  void,
  Error,
  { service: string; cardId: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ service, cardId }) => activateCard(service, cardId),
    onSuccess: (_, { service, cardId }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['card-balance', service, cardId] });
      // Invalidate native cards to update card status display
      queryClient.invalidateQueries({ queryKey: ['native-cards', service] });
      // Invalidate card requests to refresh status
      queryClient.invalidateQueries({ queryKey: ['card-requests', service] });
    },
    onError: (error: Error) => {
      // Extract API error message from the error response
      let errorMessage = 'Failed to activate card';

      try {
        // Check if the error has a response property with data
        const errorWithResponse = error as Error & { response?: { data?: { message?: string } } };
        if (errorWithResponse.response?.data?.message) {
          errorMessage = errorWithResponse.response.data.message;
        } else if (error.message) {
          // Fallback to the error message if no response data
          errorMessage = error.message;
        }
      } catch {
        // If parsing fails, use the default message
        errorMessage = 'Failed to activate card';
      }

      // Show error toast with the API message
      toast.error(errorMessage);
    },
  });
}

/**
 * Hook to deactivate a card
 * @returns {UseMutationResult} Mutation result for deactivating a card
 */
export function useDeactivateCard(): UseMutationResult<
  void,
  Error,
  { service: string; cardId: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ service, cardId }) => deactivateCard(service, cardId),
    onSuccess: (_, { service, cardId }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['card-balance', service, cardId] });
      // Invalidate native cards to update card status display
      queryClient.invalidateQueries({ queryKey: ['native-cards', service] });
    },
  });
}

/**
 * Hook to freeze an active card
 * @returns {UseMutationResult} Mutation result for freezing a card
 */
export function useFreezeCard(): UseMutationResult<
  void,
  Error,
  { service: string; cardId: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ service, cardId }) => freezeCard(service, cardId),
    onSuccess: (_, { service, cardId }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['card-balance', service, cardId] });
      // Invalidate native cards to update card status display
      queryClient.invalidateQueries({ queryKey: ['native-cards', service] });
      // Invalidate card requests to refresh status
      queryClient.invalidateQueries({ queryKey: ['card-requests', service] });
    },
    onError: (error: Error) => {
      // Extract API error message from the error response
      let errorMessage = 'Failed to freeze card';

      try {
        // Check if the error has a response property with data
        const errorWithResponse = error as Error & { response?: { data?: { message?: string } } };
        if (errorWithResponse.response?.data?.message) {
          errorMessage = errorWithResponse.response.data.message;
        } else if (error.message) {
          // Fallback to the error message if no response data
          errorMessage = error.message;
        }
      } catch {
        // If parsing fails, use the default message
        errorMessage = 'Failed to freeze card';
      }

      // Show error toast with the API message
      toast.error(errorMessage);
    },
  });
}

/**
 * Hook to unfreeze a card that was previously frozen by the customer
 * @returns {UseMutationResult} Mutation result for unfreezing a card
 */
export function useUnfreezeCard(): UseMutationResult<
  void,
  Error,
  { service: string; cardId: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ service, cardId }) => unfreezeCard(service, cardId),
    onSuccess: (_, { service, cardId }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['card-balance', service, cardId] });
      // Invalidate native cards to update card status display
      queryClient.invalidateQueries({ queryKey: ['native-cards', service] });
      // Invalidate card requests to refresh status
      queryClient.invalidateQueries({ queryKey: ['card-requests', service] });
    },
    onError: (error: Error) => {
      // Extract API error message from the error response
      let errorMessage = 'Failed to unfreeze card';

      try {
        // Check if the error has a response property with data
        const errorWithResponse = error as Error & { response?: { data?: { message?: string } } };
        if (errorWithResponse.response?.data?.message) {
          errorMessage = errorWithResponse.response.data.message;
        } else if (error.message) {
          // Fallback to the error message if no response data
          errorMessage = error.message;
        }
      } catch {
        // If parsing fails, use the default message
        errorMessage = 'Failed to unfreeze card';
      }

      // Show error toast with the API message
      toast.error(errorMessage);
    },
  });
}

/**
 * Hook to create a card top-up request
 * @returns {UseMutationResult} Mutation result for creating a card top-up
 */
export function useCreateCardTopUp(): UseMutationResult<
  void,
  Error,
  { service: string; cardId: string; topUpData: CreateCardTopUpRequest }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ service, cardId, topUpData }) => createCardTopUp(service, cardId, topUpData),
    onSuccess: (_, { service, cardId }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['card-balance', service, cardId] });
      queryClient.invalidateQueries({ queryKey: ['card-topup-requests', service] });
      queryClient.invalidateQueries({ queryKey: ['card-transactions', service, cardId] });
      // Also invalidate wallet balance to reflect the pending transaction
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
    },
    onError: (error: Error) => {
      // Extract API error message from the error response
      let errorMessage = 'Failed to create card top-up';

      try {
        // Check if the error has a response property with data
        const errorWithResponse = error as Error & { response?: { data?: { message?: string } } };
        if (errorWithResponse.response?.data?.message) {
          errorMessage = errorWithResponse.response.data.message;
        } else if (error.message) {
          // Fallback to the error message if no response data
          errorMessage = error.message;
        }
      } catch {
        // If parsing fails, use the default message
        errorMessage = 'Failed to create card top-up';
      }

      // Show error toast with the API message
      toast.error(errorMessage);
    },
  });
}

/**
 * Hook to update card top-up status
 * @returns {UseMutationResult} Mutation result for updating card top-up status
 */
export function useUpdateCardTopUpStatus(): UseMutationResult<
  void,
  Error,
  { service: string; cardTopUpId: string; statusData: UpdateCardTopUpStatusRequest }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ service, cardTopUpId, statusData }) =>
      updateCardTopUpStatus(service, cardTopUpId, statusData),
    onSuccess: (_, { service }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['card-topup-requests', service] });
    },
  });
}

/**
 * Hook to fetch card top-up requests
 * @param {string} service - The service type for the card (e.g., "eps")
 * @param {CardTopUpRequestsFilters} filters - Optional filters for the request
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for card top-up requests list
 */
export function useCardTopUpRequests(
  service: string,
  filters: CardTopUpRequestsFilters = {},
  enabled: boolean = true
): UseQueryResult<CardTopUpRequest[], Error> {
  return useQuery({
    queryKey: ['card-topup-requests', service, filters],
    queryFn: () => getCardTopUpRequests(service, filters),
    enabled: enabled && !!service,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * Hook to fetch native cards for a specific service
 * @param {string} service - The service type for the cards (e.g., "eps")
 * @param {NativeCardsFilters} filters - Optional filters for pagination
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for native cards list
 */
export function useNativeCards(
  service: string,
  filters: NativeCardsFilters = {},
  enabled: boolean = true
): UseQueryResult<NativeCard[], Error> {
  return useQuery({
    queryKey: ['native-cards', service, filters],
    queryFn: () => getNativeCards(service, filters),
    enabled: enabled && !!service,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * Hook to fetch card transactions for a specific card
 * @param {string} service - The service type for the card (e.g., "eps")
 * @param {string} cardId - The ID of the card
 * @param {CardTransactionsFilters} filters - Optional filters for the request
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for card transactions list with pagination
 */
export function useCardTransactions(
  service: string,
  cardId: string,
  filters: CardTransactionsFilters = {},
  enabled: boolean = true
): UseQueryResult<{ data: CardTransaction[]; pagination: CardTransactionsPagination }, Error> {
  return useQuery({
    queryKey: ['card-transactions', service, cardId, filters],
    queryFn: () => getCardTransactions(service, cardId, filters),
    enabled: enabled && !!service && !!cardId,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * Hook to export card transactions as a CSV or PDF file and download it
 * @returns {UseMutationResult} Mutation result for exporting card transactions
 */
export function useExportCardTransactions(): UseMutationResult<
  Blob,
  Error,
  ExportCardTransactionsRequest
> {
  return useMutation({
    mutationFn: exportCardTransactions,
    onSuccess: (blob, { format }) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `card-transactions-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('The file has been exported successfully.');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to export card transactions');
    },
  });
}
